import React, { FC } from 'react';
import FormBlock from 'src/components/FormBlock';
import { ProviderType } from 'src/types/provider';
import Detail from './Detail';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Transport from './Transport';
import { SelectValueType } from 'src/components/Select';
import Users from './Users';
import Contract from './Contract';
import Sla from './Sla';
import It from './It';
import History from './History';
import { UserType } from 'src/types/user';
import { Stack } from '@mui/material';
import Button from 'src/components/Button';
import translate from '@translate';
import { EditKeysType, tabs } from './constants';
import ProgressNew from './ProgressNew';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { ApplicationInstallType } from 'src/types/ApplicationInstall';

export type OnChangeMultipleType = (updates: Partial<ProviderType>) => void;

export interface RenderComponentsPropsType {
    data: ProviderType | null;
    usersData: UserType[] | null;
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    editData: ProviderType;
    editErrors: FormErrorsType;
    newProvider: boolean;
    disableEdit: boolean;
    serviceCodes: ConstantsCodeType[];
    shipperCodes: ConstantsCodeType[];
    providerSystemCodes: ConstantsCodeType[];
    providersCodes: ConstantsCodeType[];
    clientTypeCodes: ConstantsCodeType[];
    businessFieldCodes: ConstantsCodeType[];
    contractTermCodes: ConstantsCodeType[];
    activeTab: string;
    selectedServiceCodes: SelectValueType[];
    setSelectedServiceCodes: React.Dispatch<React.SetStateAction<SelectValueType[]>>;
    handleChangeTab: (value: string, requireValidation?: boolean) => void;
    handleSave: () => void;
    permissionOnboardingEdit: boolean;
    permissionFiles: boolean;
    salesRepresentativeCodes: ConstantsCodeType[];
    keyAccountManagerCodes: ConstantsCodeType[];
    providerStatusCodes: ConstantsCodeType[];
    applicationInstallData: ApplicationInstallType | null;
}

const RenderComponents: FC<RenderComponentsPropsType> = (props) => {
    // Filter tabs for new providers - exclude history tab only
    const getFilteredTabs = () => {
        return tabs.filter(tab => {
            if (tab.value === 'history' && props.newProvider) {
                return false;
            }
            return true;
        });
    };

    const renderStepsMovement = () => {
        const filteredTabs = getFilteredTabs();
        const activeStepIndex = filteredTabs.findIndex((item) => item.value === props.activeTab);
        return (
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="center" sx={{ marginTop: 2 }}>
                {!!activeStepIndex && (
                    <Button variant="outlined" onClick={() => props.handleChangeTab(filteredTabs[activeStepIndex - 1].value)}>
                        {translate('providerDetail.action.prevStep')}
                    </Button>
                )}
                {activeStepIndex + 1 !== filteredTabs.length && (
                    <Button onClick={() => props.handleChangeTab(filteredTabs[activeStepIndex + 1].value, true)}>
                        {translate('providerDetail.action.nextStep')}
                    </Button>
                )}
                {activeStepIndex + 1 === filteredTabs.length && (
                    <Button onClick={() => props.handleSave()}>{translate('providerDetail.action.create')}</Button>
                )}
            </Stack>
        );
    };

    const renderDetail = () => (
        <FormBlock>
            <Detail
                data={props.data}
                onChange={props.onChange}
                onChangeMultiple={props.onChangeMultiple}
                editData={props.editData}
                editErrors={props.editErrors}
                newProvider={props.newProvider}
                disableEdit={props.disableEdit}
                providerSystemCodes={props.providerSystemCodes}
                permissionOnboardingEdit={props.permissionOnboardingEdit}
                providersCodes={props.providersCodes}
                clientTypeCodes={props.clientTypeCodes}
                businessFieldCodes={props.businessFieldCodes}
                salesRepresentativeCodes={props.salesRepresentativeCodes}
                keyAccountManagerCodes={props.keyAccountManagerCodes}
            />
        </FormBlock>
    );

    const renderContract = () => (
        <FormBlock>
            <Contract
                onChange={props.onChange}
                editData={props.editData}
                editErrors={props.editErrors}
                disableEdit={props.disableEdit}
                contractTermCodes={props.contractTermCodes}
                permissionFiles={props.permissionFiles}
            />
        </FormBlock>
    );

    const renderSla = () => (
        <FormBlock>
            <Sla
                onChange={props.onChange}
                editData={props.editData}
                editErrors={props.editErrors}
                disableEdit={props.disableEdit}
            />
        </FormBlock>
    );

    const renderIt = () => (
        <FormBlock>
            <It
                onChange={props.onChange}
                onChangeMultiple={props.onChangeMultiple}
                editData={props.editData}
                editErrors={props.editErrors}
                disableEdit={props.disableEdit}
                providerSystemCodes={props.providerSystemCodes}
                providersCodes={props.providersCodes}
                providerStatusCodes={props.providerStatusCodes}
                permissionOnboardingEdit={props.permissionOnboardingEdit}
                newProvider={props.newProvider}
                applicationInstallData={props.applicationInstallData}
            />
        </FormBlock>
    );

    const renderTransport = () => (
        <FormBlock>
            <Transport
                onChange={props.onChange}
                editData={props.editData}
                disableEdit={props.disableEdit}
                serviceCodes={props.serviceCodes}
                shipperCodes={props.shipperCodes}
                selectedServiceCodes={props.selectedServiceCodes}
                setSelectedServiceCodes={props.setSelectedServiceCodes}
            />
        </FormBlock>
    );

    const renderUsers = () => (
        <FormBlock>
            <Users
                data={props.data}
                usersData={props.usersData}
                onChange={props.onChange}
                editData={props.editData}
                disableEdit={props.disableEdit}
            />
        </FormBlock>
    );

    const renderHistory = () => (
        <History
            providerId={props.data?.ID}
        />
    );

    const getActiveTab = () => {
        switch (props.activeTab) {
            case 'detail':
                return renderDetail();
            case 'contract':
                return renderContract();
            case 'sla':
                return renderSla();
            case 'it':
                return renderIt();
            case 'transport':
                return renderTransport();
            case 'users':
                return renderUsers();
            case 'history':
                // Don't render history tab for new providers
                return props.newProvider ? null : renderHistory();
            default:
                return null;
        }
    };

    if (!props.newProvider) {
        return getActiveTab();
    } else {
        const filteredTabs = getFilteredTabs();
        return (
            <>
                <ProgressNew activeTab={props.activeTab} tabs={filteredTabs} />
                {getActiveTab()}
                {renderStepsMovement()}
            </>
        );
    }
};

export default RenderComponents;
